/**
 * DataFlowMap — Interactive data flow visualization for System Pulse.
 *
 * Shows all data sources, sync jobs, DB tables, and frontend modules
 * with connection lines and hover/click detail panels.
 *
 * @component DataFlowMap
 * @parent SystemPulse.jsx (Datenfluss tab)
 */
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';

// ─── Color Config ─────────────────────────────────────────────

const COLORS = {
  blue:   { bg: 'bg-blue-50 dark:bg-blue-500/10',   border: 'border-blue-200 dark:border-blue-500/30',   text: 'text-blue-800 dark:text-blue-300',   stroke: '#338edc', fill: '#EBF4FD', fillDark: 'rgba(51,142,220,0.1)' },
  teal:   { bg: 'bg-teal-50 dark:bg-teal-500/10',   border: 'border-teal-200 dark:border-teal-500/30',   text: 'text-teal-800 dark:text-teal-300',   stroke: '#14B8A6', fill: '#F0FDFA', fillDark: 'rgba(20,184,166,0.1)' },
  orange: { bg: 'bg-amber-50 dark:bg-amber-500/10',  border: 'border-amber-200 dark:border-amber-500/30', text: 'text-amber-800 dark:text-amber-300', stroke: '#F59E0B', fill: '#FFFBEB', fillDark: 'rgba(245,158,11,0.1)' },
  purple: { bg: 'bg-purple-50 dark:bg-purple-500/10', border: 'border-purple-200 dark:border-purple-500/30', text: 'text-purple-800 dark:text-purple-300', stroke: '#8B5CF6', fill: '#FAF5FF', fillDark: 'rgba(139,92,246,0.1)' },
  gray:   { bg: 'bg-gray-50 dark:bg-gray-500/10',   border: 'border-[var(--border-default,#E5E7EB)] dark:border-gray-500/30',   text: 'text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)]',   stroke: '#6B7280', fill: '#F9FAFB', fillDark: 'rgba(107,114,128,0.1)' },
};

// ─── Data Definitions ─────────────────────────────────────────

const SOURCES = [
  {
    id: 'odoo_weship', label: 'WeShip Odoo', subtitle: 'Rechnungen, Kunden, Lager', color: 'blue',
    detail: {
      title: 'WeShip Odoo (ERP)',
      description: 'Primaere Geschaeftsdatenquelle. Verbindung via XML-RPC API zu Odoo 17.',
      provides: ['Kundenstammdaten (Name, Adresse, Segment, Odoo-ID)', 'Rechnungen + Billing Lines (Fulfillment, Lager, Carrier, Handling)', 'Lagervolumen pro Kunde (m\u00B3 + \u20AC aus Stock Volume Billing)', 'Mitarbeiterdaten (Name, Abteilung, SVNR)', 'Partner/Merchant-Zuordnungen'],
      connection: 'XML-RPC, Credentials in .env (ODOO_URL, ODOO_DB, ODOO_USER, ODOO_PASSWORD)',
      frequency: 'Abhaengig vom Sync-Job: merchant_sync 2x/Tag, billing_tracker 1x/Tag',
      issues: 'Antwortet manchmal mit HTML statt JSON. Feld exclude_from_invoice_tab fehlt \u2192 Kunden-Umsatz 500er.',
      owner: 'Jakob / WeShip IT',
    }
  },
  {
    id: 'legacy_dev', label: 'Legacy Dev Server', subtitle: 'Waves, Packs, Performance', color: 'teal',
    detail: {
      title: 'Legacy Dev Server (Odoo)',
      description: 'Zweite Odoo-Instanz mit operativen Fulfillment-Daten. Enthaelt die tatsaechlichen Pick- und Pack-Vorgaenge.',
      provides: ['wave_stats: Pick-Vorgaenge (total_picks, total_skus, total_articles, duration)', 'pack_stats: Pack-Vorgaenge (package_count, sku_count, article_count, total_weight, duration)', 'Warehouse Location Stats', 'Employee-Zuordnungen zu Waves/Packs'],
      connection: 'XML-RPC, separater Odoo-Server. Credentials: LEGACY_ODOO_*',
      frequency: 'wave_stats: stuendlich, pack_stats: alle 15 Min',
      issues: 'Daten-Luecke Waves ab 02.02.2026, Pack-Stats Luecke 01.-08.02.2026. Sync brach ab wegen warehouse_location_stats Schema-Aenderung.',
      owner: 'Jakob',
    }
  },
  {
    id: 'oms_helpdesk', label: 'OMS Helpdesk', subtitle: 'Cases, Tickets', color: 'orange',
    detail: {
      title: 'OMS Helpdesk (Dynaflow)',
      description: 'Customer Support Ticketsystem. Cases werden von Endkunden erstellt und in WeShip synchronisiert.',
      provides: ['Support-Cases (Titel, Beschreibung, Stage, Prioritaet)', 'Case-Nachrichten (Konversation Customer \u2194 Agent)', 'Stage-Aenderungen und Timestamps'],
      connection: 'XML-RPC zu portal.development.dynaflow.at. Credentials: OMS_*',
      frequency: 'oms_sync: alle 15 Min',
      issues: 'Instabil \u2014 haeufige 502 Nginx Timeouts. Graceful Fallback: lokale Cases werden angezeigt wenn OMS nicht erreichbar.',
      owner: 'Jakob / Dynaflow',
    }
  },
  {
    id: 'email_imap', label: 'E-Mail (IMAP)', subtitle: 'help@room4.at', color: 'orange',
    detail: {
      title: 'IMAP E-Mail Eingang',
      description: 'Eingehende Support-E-Mails werden automatisch als Cases erfasst.',
      provides: ['Neue Cases aus eingehenden E-Mails', 'Follow-Up Nachrichten zu bestehenden Cases (Thread-Erkennung via In-Reply-To)', 'Kunden-E-Mail-Adressen (automatische Zuordnung)'],
      connection: 'IMAP: help@room4.at, SMTP fuer Antworten. Separater Cases-SMTP.',
      frequency: 'imap_polling: alle 5 Min',
      issues: 'E-Mails landen manchmal im Spam. Reply-Body Parsing: Zitierter Text wird via >>>-Pattern entfernt.',
      owner: 'WeShip Support Team',
    }
  },
];

const SYNCS = [
  { id: 'merchant_sync', label: 'merchant_sync', color: 'blue', source: 'odoo_weship', targets: ['kunden'],
    detail: { title: 'Merchant Sync', description: 'Synchronisiert Kundenstammdaten aus WeShip Odoo nach kunden-Tabelle.', logic: 'Liest alle aktiven Partner aus Odoo (res.partner mit is_company=true). Mapped: Odoo-ID \u2192 kunden.odoo_id.', schedule: '2x taeglich (06:00, 18:00)', writesTo: 'kunden (356 Zeilen)', serviceFile: 'backend/src/services/merchant-sync.js (189 Z.)' }},
  { id: 'billing_tracker', label: 'billing_tracker', color: 'blue', source: 'odoo_weship', targets: ['deckungsbeitrag_cache'],
    detail: { title: 'Billing Tracker', description: 'Holt Rechnungsdaten aus Odoo und cached sie fuer die Deckungsbeitrags-Berechnung.', logic: 'Liest account.move.line aus Odoo. Kategorisiert in: Fulfillment, Lager, Carrier, Handling, SOP.', schedule: '1x taeglich (05:00)', writesTo: 'deckungsbeitrag_cache (156.909 Zeilen)', serviceFile: 'backend/src/services/billing-tracker.js (348 Z.)' }},
  { id: 'lager_volumen_sync', label: 'lager_volumen', color: 'blue', source: 'odoo_weship', targets: ['kunden'],
    detail: { title: 'Lagervolumen Sync', description: 'Liest Lagervolumen (m\u00B3 + \u20AC) aus Odoo Billing fuer jeden Kunden.', logic: 'Holt Stock Volume Billing Lines aus Odoo. 131 Kunden haben Lagerdaten.', schedule: 'Taeglich', writesTo: 'kunden (lager_m3, lager_eur Spalten)', serviceFile: 'Cronjob in backend' }},
  { id: 'employee_sync', label: 'employee_sync', color: 'blue', source: 'odoo_weship', targets: ['mitarbeiter'],
    detail: { title: 'Employee Sync', description: 'Synchronisiert Mitarbeiterdaten aus WeShip Odoo.', logic: 'Liest hr.employee aus Odoo. Mapped: Name, Abteilung, Odoo-ID, SVNR.', schedule: 'Taeglich 06:00', writesTo: 'mitarbeiter (45 Zeilen)', serviceFile: 'backend/src/services/employee-sync.js (442 Z.)' }},
  { id: 'wave_stats_sync', label: 'wave_stats', color: 'teal', source: 'legacy_dev', targets: ['waves'],
    detail: { title: 'Wave Stats Sync', description: 'Synchronisiert Pick-Vorgaenge (Waves) vom Legacy Dev Server.', logic: 'Liest stock.picking.wave aus Legacy-Odoo. Duration = MINUTEN! SOP-Erkennung via wave_name ILIKE \'%sop%\'.', schedule: 'Stuendlich', writesTo: 'waves (11.355 Zeilen)', serviceFile: 'backend/src/services/wave-stats.js (529 Z.)', important: 'Duration = MINUTEN. Shared Accounts: outbound_de, inbound_de_2.' }},
  { id: 'pack_stats_sync', label: 'pack_stats', color: 'teal', source: 'legacy_dev', targets: ['pack_stats_table'],
    detail: { title: 'Pack Stats Sync', description: 'Synchronisiert Pack-Vorgaenge vom Legacy Dev Server. Kernquelle fuer Fulfillment-Metriken.', logic: 'Liest stock.pack.operation. Pro Zeile: package_count, sku_count, article_count, total_weight, duration (MINUTEN!).', schedule: 'Alle 15 Minuten', writesTo: 'pack_stats (151.000+ Zeilen)', serviceFile: 'backend/src/services/wave-stats.js', important: 'Basis fuer: Leaderboard, Performance, Forecast IST, Kundenportal Stats.' }},
  { id: 'oms_sync', label: 'oms_sync', color: 'orange', source: 'oms_helpdesk', targets: ['cases_table'],
    detail: { title: 'OMS Case Sync', description: 'Synchronisiert Support-Cases aus dem OMS Helpdesk System.', logic: 'Liest helpdesk.ticket aus OMS-Odoo. Mapped Stages. Graceful Fallback bei OMS-Ausfall.', schedule: 'Alle 15 Min', writesTo: 'cases, case_nachrichten', serviceFile: 'backend/src/services/oms-odoo.js (333 Z.)', important: 'OMS instabil (502 Nginx). Auto-Retry implementiert.' }},
  { id: 'imap_polling', label: 'imap_polling', color: 'orange', source: 'email_imap', targets: ['cases_table'],
    detail: { title: 'IMAP E-Mail Polling', description: 'Pollt eingehende E-Mails und erstellt/aktualisiert Cases.', logic: 'Verbindet via IMAP zu help@room4.at. Neue E-Mails \u2192 neuer Case. Thread-ID \u2192 bestehender Case.', schedule: 'Alle 5 Min', writesTo: 'cases, case_nachrichten, kunden_email_adressen', serviceFile: 'backend/src/services/inbound-email.js (1.264 Z.)' }},
  { id: 'forecast_ist_sync', label: 'forecast_ist', color: 'purple', source: 'pack_stats_table', targets: ['tages_forecasts'],
    detail: { title: 'Forecast IST Sync', description: 'Schreibt echte IST-Paketzahlen aus pack_stats in tages_forecasts.ist_pakete.', logic: 'UPDATE tages_forecasts SET ist_pakete = SUM(pack_stats.package_count) pro Kunde pro Tag. Backfill: 60 Tage.', schedule: 'Alle 15 Min', writesTo: 'tages_forecasts.ist_pakete', serviceFile: 'backend/src/routes/forecast.js', important: 'Vorher fehlte dieser Sync komplett \u2014 IST war immer 0 im Grid.' }},
  { id: 'forecast_actuals', label: 'forecast_actuals', color: 'purple', source: 'pack_stats_table', targets: ['customer_forecast_actuals'],
    detail: { title: 'Forecast Actuals (Portal)', description: 'Schreibt IST-Daten fuer das Kundenportal in customer_forecast_actuals.', logic: 'INSERT INTO customer_forecast_actuals SELECT kunde_id, date, SUM(package_count) FROM pack_stats.', schedule: 'Taeglich 06:00', writesTo: 'customer_forecast_actuals', serviceFile: 'backend/src/routes/customer-forecasts.js', important: 'Getrennt vom forecast_ist_sync! Dieses ist fuer Portal-Kunden.' }},
  { id: 'sla_check', label: 'sla_check', color: 'purple', source: 'cases_table', targets: ['cases_table'],
    detail: { title: 'SLA Check', description: 'Prueft SLA-Einhaltung fuer alle offenen Cases.', logic: 'Berechnet verbleibende SLA-Zeit: Standard 24h, Premium 6h, Express 2h. Setzt SLA-Status + Eskalation.', schedule: 'Alle 15 Min', writesTo: 'cases (sla_* Felder), case_sla_log', serviceFile: 'backend/src/services/sla-engine.js (677 Z.)' }},
];

const TABLES = [
  { id: 'kunden', label: 'kunden', rows: '356', color: 'blue',
    detail: { title: 'kunden (356 Zeilen)', description: 'Zentrale Kundenstamm-Tabelle.', keyColumns: 'id, name, odoo_id, oms_name, segment (A/B/C), lager_m3, lager_eur, fulfillment_status', fedBy: ['merchant_sync', 'lager_volumen_sync'], usedBy: ['Dashboard', 'Deckungsbeitrag', 'Forecast', 'Kundenportal', 'Sales CRM'] }},
  { id: 'deckungsbeitrag_cache', label: 'db_cache', rows: '157k', color: 'blue',
    detail: { title: 'deckungsbeitrag_cache (156.909 Zeilen)', description: 'Gecachte Billing-Daten fuer Deckungsbeitrags-Berechnung.', keyColumns: 'kunde_id, monat, kategorie (Fulfillment/Lager/Carrier/Handling/SOP), betrag', fedBy: ['billing_tracker'], usedBy: ['Deckungsbeitrag', 'Kunden-Detail'], important: 'Personalkosten nur aus eigener Berechnung (nie aus Billing). Stundensatz: 27\u20AC/h.' }},
  { id: 'waves', label: 'waves', rows: '11.4k', color: 'teal',
    detail: { title: 'waves (11.355 Zeilen, ab 29.08.2025)', description: 'Pick-Vorgaenge aus dem Lager.', keyColumns: 'wave_name, employee_id, total_picks, total_skus, total_articles, duration (MIN!)', fedBy: ['wave_stats_sync'], usedBy: ['Performance', 'Leaderboard', 'Idle-Time', 'Operations'], important: 'SOP-Erkennung: wave_name ILIKE \'%sop%\'. Luecke ab 02.02.2026.' }},
  { id: 'pack_stats_table', label: 'pack_stats', rows: '151k', color: 'teal',
    detail: { title: 'pack_stats (151.000+ Zeilen, ab 27.08.2025)', description: 'Pack-Vorgaenge. Wichtigste operative Tabelle.', keyColumns: 'kunde_id, merchant_id, package_count, sku_count, article_count, total_weight, duration (MIN!)', fedBy: ['pack_stats_sync'], usedBy: ['Performance', 'Leaderboard', 'Forecast (IST)', 'Kundenportal Stats', 'Operations', 'Idle-Time'], important: 'Basis fuer fast alle operativen Metriken. Pack Efficiency = articles/min.' }},
  { id: 'cases_table', label: 'cases', rows: '~50', color: 'orange',
    detail: { title: 'cases + case_nachrichten', description: 'Support-Cases aus OMS und E-Mail.', keyColumns: 'titel, beschreibung, stage, prioritaet, kunde_id, zugewiesen_an, sla_*, quelle', fedBy: ['oms_sync', 'imap_polling'], usedBy: ['Cases Board', 'Cases Liste', 'Cases Reports', 'Kundenportal'], important: '6 Stages mit Custom Labels. SLA-Klassen: Standard 24h, Premium 6h, Express 2h.' }},
  { id: 'mitarbeiter', label: 'mitarbeiter', rows: '45', color: 'purple',
    detail: { title: 'mitarbeiter (45 Zeilen)', description: 'Mitarbeiterstammdaten.', keyColumns: 'name, odoo_id, abteilung, svnr_prefix', fedBy: ['employee_sync'], usedBy: ['Team-Uebersicht', 'Leaderboard', 'Schichtplanung', 'MA-Portal', 'Performance'], important: 'Shared Accounts (outbound_de, inbound_de_2) = mehrere Personen.' }},
  { id: 'tages_forecasts', label: 'tages_forecasts', rows: '~500', color: 'purple',
    detail: { title: 'tages_forecasts', description: 'Tages-SOLL und IST pro Kunde.', keyColumns: 'kunde_id, datum, soll_pakete, ist_pakete, updated_at', fedBy: ['forecast_ist_sync (IST)', 'Forecast Grid-Wizard (SOLL)'], usedBy: ['Forecast Grid', 'Kundenportal Forecasts'], important: 'IST wird alle 15 Min aus pack_stats gesynct. SOLL kommt aus manueller Eingabe.' }},
  { id: 'monats_forecasts', label: 'monats_forecasts', rows: '~200', color: 'purple',
    detail: { title: 'monats_forecasts', description: 'Monats-SOLL pro Kunde.', keyColumns: 'kunde_id, monat, soll_pakete, quelle (intern/manuell/kunde)', fedBy: ['CS-Manager (intern)', 'Kundenportal Approval Sync'], usedBy: ['Forecast Uebersicht', 'Kundenportal', 'FTE-Berechnung'], important: 'Datenfluss: Kunde Portal \u2192 customer_forecasts \u2192 approved \u2192 sync \u2192 monats_forecasts.' }},
  { id: 'customer_forecast_actuals', label: 'forecast_actuals', rows: '~500', color: 'purple',
    detail: { title: 'customer_forecast_actuals', description: 'IST-Daten speziell fuer das Kundenportal.', keyColumns: 'kunden_id, date, actual_parcels, synced_at', fedBy: ['forecast_actuals Cronjob'], usedBy: ['Kundenportal Forecasts', 'Kundenportal Dashboard'], important: 'Getrennt von tages_forecasts! NUR fuer Portal-Kunden.' }},
];

const MODULES = [
  { id: 'mod_dashboard', label: 'Dashboard', color: 'gray',
    detail: { title: 'Dashboard (Home)', description: '18+ Widget-Typen mit Drag & Drop Layout. AI KPI-Insights.', dependsOn: ['kunden', 'pack_stats_table', 'waves', 'cases_table', 'mitarbeiter'], pageFile: 'frontend/src/pages/Dashboard.jsx', route: '/' }},
  { id: 'mod_deckungsbeitrag', label: 'Deckungsbeitrag', color: 'gray',
    detail: { title: 'Deckungsbeitrag', description: 'Uebersicht + Kunden-Detail + Auftrags-Detail. SOP-Erkennung + Pauschale.', dependsOn: ['deckungsbeitrag_cache', 'kunden', 'waves', 'pack_stats_table'], pageFile: 'frontend/src/pages/Deckungsbeitrag.jsx', route: '/controlling/deckungsbeitrag', backendFile: 'backend/src/routes/deckungsbeitrag.js (1.866 Z.)' }},
  { id: 'mod_performance', label: 'Performance', color: 'gray',
    detail: { title: 'Performance & Leaderboard', description: 'Pick & Pack Statistiken, Leaderboard + Ranking, Trend-Analyse.', dependsOn: ['waves', 'pack_stats_table', 'mitarbeiter'], pageFile: 'frontend/src/pages/Performance.jsx', route: '/team/performance', backendFile: 'backend/src/routes/performance.js (1.439 Z.)' }},
  { id: 'mod_forecast', label: 'Forecast', color: 'gray',
    detail: { title: 'Forecast Grid + Wizard', description: 'Monats-Forecasts mit Tagesansicht. SOLL vs IST, Segment-Gruppierung, FTE.', dependsOn: ['tages_forecasts', 'monats_forecasts', 'pack_stats_table', 'kunden'], pageFile: 'frontend/src/pages/Forecast.jsx', route: '/planung/forecast', backendFile: 'backend/src/routes/forecast.js (1.583 Z.)' }},
  { id: 'mod_cases', label: 'Cases', color: 'gray',
    detail: { title: 'Cases & Support', description: 'Kanban Board + Listen-View. SLA-Tracking. E-Mail Ticketsystem. OMS Integration.', dependsOn: ['cases_table'], pageFile: 'frontend/src/pages/Cases*.jsx', route: '/cases', backendFile: 'backend/src/routes/cases.js (3.237 Z.)' }},
  { id: 'mod_operations', label: 'Operations', color: 'gray',
    detail: { title: 'Operations', description: 'Echtzeit-Operations mit KPIs und Charts. Fulfillment-Stats + Historie.', dependsOn: ['pack_stats_table', 'waves', 'kunden'], pageFile: 'frontend/src/pages/Operations.jsx', route: '/operations', important: 'BUG: KPIs zeigen 0 \u2014 Fulfillment Snapshots unvollstaendig.' }},
  { id: 'mod_sales', label: 'Sales CRM', color: 'gray',
    detail: { title: 'Sales CRM', description: 'Lead Pipeline, E-Mail Integration, Onboarding, Workflow Engine, AI Sales Agent.', dependsOn: ['kunden'], pageFile: 'frontend/src/pages/Sales*.jsx', route: '/sales', backendFile: 'backend/src/routes/sales.js (2.378 Z.)' }},
  { id: 'mod_kundenportal', label: 'Kundenportal', color: 'gray',
    detail: { title: 'Kundenportal', description: 'Dashboard, Forecasts, Performance, Rechnungen. Durchgaengig duzen. Eigene Sidebar.', dependsOn: ['kunden', 'monats_forecasts', 'tages_forecasts', 'customer_forecast_actuals', 'pack_stats_table'], pageFile: 'frontend/src/pages/Portal*.jsx', route: '/portal', backendFile: 'backend/src/routes/portal.js (1.959 Z.)' }},
  { id: 'mod_leaderboard', label: 'Leaderboard', color: 'gray',
    detail: { title: 'Leaderboard', description: 'Ranking nach Pick/Pack Effizienz. Tages/Wochen-Ansicht.', dependsOn: ['pack_stats_table', 'waves', 'mitarbeiter'], pageFile: 'frontend/src/pages/Leaderboard.jsx', route: '/team/leaderboard' }},
  { id: 'mod_pulse', label: 'System Pulse', color: 'gray',
    detail: { title: 'System Pulse', description: 'AI-generierte Cronjob-Zusammenfassungen. 4x taeglich. Datenfluss-Visualisierung.', dependsOn: ['pack_stats_table', 'tages_forecasts', 'cases_table'], pageFile: 'frontend/src/pages/SystemPulse.jsx', route: '/entwicklung/system-pulse' }},
];

// ─── Connection graph ─────────────────────────────────────────

// Build edges: source->sync, sync->table, table->module
function buildEdges() {
  const edges = [];
  SYNCS.forEach(s => {
    edges.push({ from: s.source, to: s.id, color: s.color });
    s.targets.forEach(t => edges.push({ from: s.id, to: t, color: s.color }));
  });
  MODULES.forEach(m => {
    (m.detail.dependsOn || []).forEach(dep => {
      edges.push({ from: dep, to: m.id, color: 'gray' });
    });
  });
  return edges;
}

function buildAdjacency(edges) {
  const adj = {};
  edges.forEach(({ from, to }) => {
    if (!adj[from]) adj[from] = new Set();
    if (!adj[to]) adj[to] = new Set();
    adj[from].add(to);
    adj[to].add(from);
  });
  return adj;
}

function getConnectedNodes(nodeId, adj) {
  const visited = new Set([nodeId]);
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift();
    const neighbors = adj[current];
    if (neighbors) {
      neighbors.forEach(n => {
        if (!visited.has(n)) {
          visited.add(n);
          queue.push(n);
        }
      });
    }
  }
  return visited;
}

// ─── Layout calculation ───────────────────────────────────────

function computeLayout(containerWidth) {
  const w = Math.max(containerWidth, 800);
  const nodeW = Math.max(110, Math.min(140, (w - 80) / 10));
  const nodeH = 52;
  const syncNodeW = Math.max(100, Math.min(120, (w - 80) / 11));
  const syncNodeH = 36;
  const modNodeW = Math.max(100, Math.min(130, (w - 80) / 10));
  const modNodeH = 40;
  const pad = 20;

  // Row Y positions
  const labelH = 24;
  const rowGap = 70;
  const y0 = pad + labelH;
  const y1 = y0 + nodeH + rowGap;
  const y2 = y1 + syncNodeH + rowGap;
  const y3 = y2 + nodeH + rowGap;
  const totalH = y3 + modNodeH + pad + 10;

  const positions = {};

  // Row 0: Sources (4 items)
  const srcSpacing = (w - 2 * pad) / SOURCES.length;
  SOURCES.forEach((s, i) => {
    positions[s.id] = { x: pad + srcSpacing * i + (srcSpacing - nodeW) / 2, y: y0, w: nodeW, h: nodeH };
  });

  // Row 1: Syncs (11 items)
  const syncSpacing = (w - 2 * pad) / SYNCS.length;
  SYNCS.forEach((s, i) => {
    positions[s.id] = { x: pad + syncSpacing * i + (syncSpacing - syncNodeW) / 2, y: y1, w: syncNodeW, h: syncNodeH };
  });

  // Row 2: Tables (9 items)
  const tblSpacing = (w - 2 * pad) / TABLES.length;
  TABLES.forEach((t, i) => {
    positions[t.id] = { x: pad + tblSpacing * i + (tblSpacing - nodeW) / 2, y: y2, w: nodeW, h: nodeH };
  });

  // Row 3: Modules (10 items)
  const modSpacing = (w - 2 * pad) / MODULES.length;
  MODULES.forEach((m, i) => {
    positions[m.id] = { x: pad + modSpacing * i + (modSpacing - modNodeW) / 2, y: y3, w: modNodeW, h: modNodeH };
  });

  return { positions, width: w, height: totalH, rowYs: { y0, y1, y2, y3 }, labelH };
}

// ─── Detail Panel ─────────────────────────────────────────────

function DetailPanel({ node, onClose, t }) {
  if (!node) return null;
  const d = node.detail;
  if (!d) return null;

  const sections = [];

  if (d.description) sections.push({ label: null, content: d.description });
  if (d.provides?.length) sections.push({ label: t('pulse.dataflow.provides'), items: d.provides });
  if (d.logic) sections.push({ label: t('pulse.dataflow.logic'), content: d.logic });
  if (d.connection) sections.push({ label: t('pulse.dataflow.connection'), content: d.connection });
  if (d.frequency) sections.push({ label: t('pulse.dataflow.frequency'), content: d.frequency });
  if (d.schedule) sections.push({ label: t('pulse.dataflow.schedule'), content: d.schedule });
  if (d.writesTo) sections.push({ label: t('pulse.dataflow.writesTo'), content: d.writesTo });
  if (d.keyColumns) sections.push({ label: t('pulse.dataflow.keyColumns'), content: d.keyColumns });
  if (d.fedBy?.length) sections.push({ label: t('pulse.dataflow.fedBy'), items: d.fedBy });
  if (d.usedBy?.length) sections.push({ label: t('pulse.dataflow.usedBy'), items: d.usedBy });
  if (d.dependsOn?.length) sections.push({ label: t('pulse.dataflow.dependsOn'), items: d.dependsOn });
  if (d.serviceFile) sections.push({ label: t('pulse.dataflow.serviceFile'), content: d.serviceFile, mono: true });
  if (d.pageFile) sections.push({ label: t('pulse.dataflow.pageFile'), content: d.pageFile, mono: true });
  if (d.backendFile) sections.push({ label: t('pulse.dataflow.backendFile'), content: d.backendFile, mono: true });
  if (d.route) sections.push({ label: t('pulse.dataflow.route'), content: d.route, mono: true });
  if (d.owner) sections.push({ label: t('pulse.dataflow.owner'), content: d.owner });

  const hasIssues = d.issues;
  const hasImportant = d.important || node.detail?.important;
  const importantText = d.important || node.detail?.important;

  return (
    <div className="border border-[var(--border-card)] rounded-2xl bg-[var(--bg-card)] p-6 mt-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-base font-semibold text-[var(--text-primary)]">{d.title || node.label}</h3>
        <button
          onClick={onClose}
          className="p-1 rounded-lg hover:bg-[var(--bg-inset)] transition-colors text-[var(--text-secondary)]"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {sections.map((sec, i) => (
          <div key={i}>
            {sec.label && (
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-1">{sec.label}</p>
            )}
            {sec.content && (
              <p className={`text-sm text-[var(--text-primary)] leading-relaxed ${sec.mono ? 'font-mono text-xs' : ''}`}>{sec.content}</p>
            )}
            {sec.items && (
              <ul className="list-disc list-inside space-y-0.5">
                {sec.items.map((item, j) => (
                  <li key={j} className="text-sm text-[var(--text-primary)] leading-relaxed">{item}</li>
                ))}
              </ul>
            )}
          </div>
        ))}

        {/* Issues */}
        {hasIssues && (
          <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {t('pulse.dataflow.issues')}
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">{d.issues}</p>
          </div>
        )}

        {/* Important */}
        {hasImportant && !hasIssues && (
          <div className="rounded-xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-400 mb-1">
              {t('pulse.dataflow.important')}
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-400 leading-relaxed">{importantText}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SVG Node Components ──────────────────────────────────────

function SvgNode({ id, pos, label, subtitle, rows, color, opacity, onMouseEnter, onMouseLeave, onClick, isActive }) {
  const c = COLORS[color] || COLORS.gray;
  const borderWidth = isActive ? 2 : 1;

  return (
    <g
      style={{ opacity, cursor: 'pointer', transition: 'opacity 0.2s ease' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      <rect
        x={pos.x} y={pos.y} width={pos.w} height={pos.h}
        rx={10} ry={10}
        fill="var(--bg-card)"
        stroke={isActive ? c.stroke : 'var(--border-card)'}
        strokeWidth={borderWidth}
      />
      {/* Color accent line at top */}
      <rect x={pos.x + 8} y={pos.y} width={pos.w - 16} height={3} rx={1.5} fill={c.stroke} opacity={0.6} />
      {/* Label */}
      <text
        x={pos.x + pos.w / 2} y={pos.y + (subtitle ? pos.h / 2 - 4 : rows ? pos.h / 2 - 4 : pos.h / 2 + 1)}
        textAnchor="middle" dominantBaseline="middle"
        fill="var(--text-primary)" fontSize={11} fontWeight={600} fontFamily="DM Sans, sans-serif"
      >
        {label}
      </text>
      {/* Subtitle */}
      {subtitle && (
        <text
          x={pos.x + pos.w / 2} y={pos.y + pos.h / 2 + 10}
          textAnchor="middle" dominantBaseline="middle"
          fill="var(--text-secondary)" fontSize={9} fontFamily="DM Sans, sans-serif"
        >
          {subtitle}
        </text>
      )}
      {/* Row count for tables */}
      {rows && (
        <text
          x={pos.x + pos.w / 2} y={pos.y + pos.h / 2 + 10}
          textAnchor="middle" dominantBaseline="middle"
          fill="var(--text-tertiary)" fontSize={9} fontFamily="DM Sans, sans-serif"
        >
          {rows} rows
        </text>
      )}
    </g>
  );
}

// ─── Main Component ───────────────────────────────────────────

export default function DataFlowMap({ t }) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1000);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  // Measure container
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const edges = useMemo(() => buildEdges(), []);
  const adj = useMemo(() => buildAdjacency(edges), [edges]);
  const layout = useMemo(() => computeLayout(containerWidth), [containerWidth]);

  const activeNodeId = selectedNode || hoveredNode;
  const connectedSet = useMemo(
    () => activeNodeId ? getConnectedNodes(activeNodeId, adj) : null,
    [activeNodeId, adj]
  );

  const getOpacity = useCallback((nodeId) => {
    if (!connectedSet) return 1;
    return connectedSet.has(nodeId) ? 1 : 0.15;
  }, [connectedSet]);

  const getEdgeOpacity = useCallback((from, to) => {
    if (!connectedSet) return 0.2;
    return (connectedSet.has(from) && connectedSet.has(to)) ? 0.7 : 0.05;
  }, [connectedSet]);

  const allNodes = useMemo(() => {
    const map = {};
    SOURCES.forEach(n => map[n.id] = n);
    SYNCS.forEach(n => map[n.id] = n);
    TABLES.forEach(n => map[n.id] = n);
    MODULES.forEach(n => map[n.id] = n);
    return map;
  }, []);

  const handleNodeClick = useCallback((id) => {
    setSelectedNode(prev => prev === id ? null : id);
  }, []);

  const handleMouseEnter = useCallback((id) => {
    if (!selectedNode) setHoveredNode(id);
  }, [selectedNode]);

  const handleMouseLeave = useCallback(() => {
    if (!selectedNode) setHoveredNode(null);
  }, [selectedNode]);

  const { positions, width, height, rowYs, labelH } = layout;

  // Row labels
  const rowLabels = [
    { y: rowYs.y0 - labelH + 4, text: t('pulse.dataflow.sources') },
    { y: rowYs.y1 - labelH + 4, text: t('pulse.dataflow.syncs') },
    { y: rowYs.y2 - labelH + 4, text: t('pulse.dataflow.tables') },
    { y: rowYs.y3 - labelH + 4, text: t('pulse.dataflow.modules') },
  ];

  const selectedNodeData = activeNodeId ? allNodes[activeNodeId] : null;

  return (
    <div>
      {/* Title */}
      <div className="mb-4">
        <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('pulse.dataflow.title')}</h2>
        <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t('pulse.dataflow.subtitle')}</p>
      </div>

      {/* SVG Container */}
      <div
        ref={containerRef}
        className="border border-[var(--border-card)] rounded-2xl bg-[var(--bg-card)] overflow-hidden"
        onClick={(e) => { if (e.target === e.currentTarget || e.target.tagName === 'svg') setSelectedNode(null); }}
      >
        <svg
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          className="block"
        >
          {/* Row Labels */}
          {rowLabels.map((rl, i) => (
            <text
              key={i}
              x={20} y={rl.y}
              fill="var(--text-tertiary)" fontSize={10} fontWeight={600}
              fontFamily="DM Sans, sans-serif" textTransform="uppercase"
              letterSpacing="0.5"
            >
              {rl.text}
            </text>
          ))}

          {/* Edges */}
          {edges.map((edge, i) => {
            const fromPos = positions[edge.from];
            const toPos = positions[edge.to];
            if (!fromPos || !toPos) return null;
            const x1 = fromPos.x + fromPos.w / 2;
            const y1 = fromPos.y + fromPos.h;
            const x2 = toPos.x + toPos.w / 2;
            const y2 = toPos.y;
            const eo = getEdgeOpacity(edge.from, edge.to);
            const c = COLORS[edge.color] || COLORS.gray;
            const midY = (y1 + y2) / 2;
            return (
              <path
                key={i}
                d={`M${x1},${y1} C${x1},${midY} ${x2},${midY} ${x2},${y2}`}
                fill="none"
                stroke={c.stroke}
                strokeWidth={eo > 0.3 ? 1.5 : 1}
                opacity={eo}
                style={{ transition: 'opacity 0.2s ease, stroke-width 0.2s ease' }}
              />
            );
          })}

          {/* Source Nodes */}
          {SOURCES.map(s => (
            <SvgNode
              key={s.id} id={s.id} pos={positions[s.id]} label={s.label} subtitle={s.subtitle}
              color={s.color} opacity={getOpacity(s.id)} isActive={activeNodeId === s.id}
              onMouseEnter={() => handleMouseEnter(s.id)} onMouseLeave={handleMouseLeave}
              onClick={() => handleNodeClick(s.id)}
            />
          ))}

          {/* Sync Nodes */}
          {SYNCS.map(s => (
            <SvgNode
              key={s.id} id={s.id} pos={positions[s.id]} label={s.label}
              color={s.color} opacity={getOpacity(s.id)} isActive={activeNodeId === s.id}
              onMouseEnter={() => handleMouseEnter(s.id)} onMouseLeave={handleMouseLeave}
              onClick={() => handleNodeClick(s.id)}
            />
          ))}

          {/* Table Nodes */}
          {TABLES.map(t => (
            <SvgNode
              key={t.id} id={t.id} pos={positions[t.id]} label={t.label} rows={t.rows}
              color={t.color} opacity={getOpacity(t.id)} isActive={activeNodeId === t.id}
              onMouseEnter={() => handleMouseEnter(t.id)} onMouseLeave={handleMouseLeave}
              onClick={() => handleNodeClick(t.id)}
            />
          ))}

          {/* Module Nodes */}
          {MODULES.map(m => (
            <SvgNode
              key={m.id} id={m.id} pos={positions[m.id]} label={m.label}
              color={m.color} opacity={getOpacity(m.id)} isActive={activeNodeId === m.id}
              onMouseEnter={() => handleMouseEnter(m.id)} onMouseLeave={handleMouseLeave}
              onClick={() => handleNodeClick(m.id)}
            />
          ))}
        </svg>
      </div>

      {/* Detail Panel */}
      <DetailPanel
        node={selectedNodeData}
        onClose={() => setSelectedNode(null)}
        t={t}
      />
    </div>
  );
}
